import React, {useState} from 'react';
import styled from 'styled-components';

export type AccordionProps = {
  header: React.ReactNode;
  className?: string;
};

const ArrowIcon = styled.span<{open: boolean}>`
  width: 9px;
  height: 9px;
  border-right: 2px solid ${(p) => (p.open ? '#6b6fd6' : '#9aa0b5')};
  border-bottom: 2px solid ${(p) => (p.open ? '#6b6fd6' : '#9aa0b5')};
  border-radius: 1px;
  flex-shrink: 0;
  margin-left: 16px;
  transform: rotate(${(p) => (p.open ? '-135deg' : '45deg')});
  transition:
    transform 0.25s ease,
    border-color 0.2s ease;
`;

const Header = styled.button`
  width: 100%;
  background: none;
  border: none;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-weight: 500;
  font-size: 1.02rem;
  color: #2a2150;
  padding: 18px 22px;
  text-align: left;
  cursor: pointer;
  transition: color 0.2s ease;
  &:hover {
    color: #6b6fd6;
  }
`;

// grid-template-rows 0fr -> 1fr animates height smoothly while keeping the content
// in the DOM (good for SEO); the inner element clips the overflow during the transition.
const ContentOuter = styled.div<{open: boolean}>`
  display: grid;
  grid-template-rows: ${(p) => (p.open ? '1fr' : '0fr')};
  transition: grid-template-rows 0.28s ease;
`;

// visibility:hidden when closed pulls the still-mounted links out of the tab order
// and accessibility tree; the delayed transition keeps them visible/animating during
// the 0.28s collapse, then hides them once fully closed.
const ContentInner = styled.div<{open: boolean}>`
  overflow: hidden;
  visibility: ${(p) => (p.open ? 'visible' : 'hidden')};
  transition: visibility 0s linear ${(p) => (p.open ? '0s' : '0.28s')};
`;

const Content = styled.div`
  padding: 0 22px 20px;
  color: #555;
  line-height: 1.65;
  p {
    margin: 0;
  }
  a {
    color: #9d74b9;
  }
`;

const Container = styled.div<{open: boolean}>`
  background: #fff;
  border: 1px solid ${(p) => (p.open ? '#cfd2ee' : '#e8e8f0')};
  border-radius: 14px;
  text-align: left;
  box-shadow: ${(p) => (p.open ? '0 6px 22px rgba(60, 50, 120, 0.07)' : 'none')};
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  &:hover {
    border-color: ${(p) => (p.open ? '#cfd2ee' : '#d6d6e6')};
  }
`;

export const Accordion: React.FC<AccordionProps> = ({header, children, className}) => {
  const [open, setOpen] = useState(false);
  return (
    <Container open={open} className={className}>
      <Header type="button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>{header}</span>
        <ArrowIcon open={open} />
      </Header>
      <ContentOuter open={open}>
        <ContentInner open={open}>
          <Content>{children}</Content>
        </ContentInner>
      </ContentOuter>
    </Container>
  );
};
